import { compact as _compact, join as _join } from 'lodash';
import eventBus from './eventBus';
import querystring from 'querystring';
import store from '../store';

const HTTP_OK = 200;
const HTTP_BAD_4xx = 400;
const HTTP_UNAUTHORIZED = 401;
const BASE_API_PATH = 'api';

// this is used really only on local dev environments, when deployed, should be
// served by the backend server so api host will be itself
// this should not be used internally, but passed into the method to access.
const apiHost = process.env.VUE_APP_API_HOST || getApiHost() || '';

let isIrapo = false;

/**
 * Total hack to get the API host since it is not known where we will be deploying to.  On the swarms,
 * it will be the /irapo-web-reporting/{branch}/service.  This defined locally since you'll probably be using
 * the web container to serve for development.
 *
 * Hard coding to remove the wat/ when we are in the WAT page.
 */
function getApiHost() {
  const path = location.pathname;
  return path.replace(new RegExp('wat/$', 'gm'), '').replace(new RegExp('embed/$', 'gm'), '');
}

function getReportApiPath(type) {
  return type === 'ST' ? 'reports' : 'customReports';
}

function getKeyValueString(keyValues) {
  return querystring.stringify(keyValues);
}

function getQueryString(config) {
  if (!config || !config.queryParams) {
    return '';
  }
  const queryStringParts = [];
  if (config.queryParams) {
    queryStringParts.push(getKeyValueString(config.queryParams));

    // delete the viewtype from the query string so it's not on the URL
    delete config.queryParams.vt;
  }

  if (isIrapo) {
    queryStringParts.push('ir=1');
  }

  return `?${_join(_compact(queryStringParts), '&')}`;
}

/**
 * Gets the headers to be sent on the fetch request.  Will automatically add the
 * session web token if one is available.  Currently default is using json
 * as the Content-Type.
 *
 * Pass in a jwtToken if you want to use this specific one.
 */
function getHeaders(jwtToken) {
  const authHeader = jwtToken || store.getters['session/sessionWebToken'] || null;
  const headers = {
    'Content-Type': 'application/json'
  };

  if (authHeader) {
    headers.Authorization = `Bearer ${authHeader}`;
  }
  return headers;
}

/**
 * Attach the base API host name if it's a relative URL, otherwise just use the absolute
 * URL passed in.
 */
function getUrl(hostToUse, url) {
  return (url || '').indexOf('http') >= 0 ? url : `${hostToUse}${url}`;
}

function getRequestOptions(config) {
  const options = {
    headers: getHeaders(config ? config.jwtToken : null),
    credentials: 'omit'
  }

  if (config && config.credentials) {
    options.credentials = config.credentials;
  }

  if (config && config.signal) {
    options.signal = config.signal;
  }

  return options;
}

/**
 * Parses the response from the API
 */
async function handleResponse(response) {
  const responseType = response.headers.get('Content-Type');
  const isJson = responseType?.indexOf('application/json') >= 0;
  const isText = responseType?.indexOf('text/plain') >= 0;
  if (response.status >= HTTP_OK && response.status < HTTP_BAD_4xx) {
    if (isJson) {
      return response.json();
    }
    else if (isText) {
      return response.text();
    }
    else {
      const bufferArray = await response.arrayBuffer();
      return Promise.resolve({
        type: responseType,
        bufferArray
      });
    }
  }
  else {
    // need to throw an error so try to get the body out
    let responseBody = null;
    if (isJson) {
      responseBody = await response.json();
    }
    else if (isText) {
      responseBody = await response.text();
    }
    else {
      responseBody = await response.arrayBuffer();
    }

    // if it's a 401, we'll clear out their session.  this can happen if they put their computer to sleep
    // so it couldn't refresh the token
    if (response.status === HTTP_UNAUTHORIZED) {
      // show a modal if they were logged in and now getting a 401
      if (store.getters['session/sessionIsLoggedIn']) {
        eventBus.bus.$emit(eventBus.CHANNEL_BUS_MESSAGE, {
          message: eventBus.MESSAGES.SESSION_EXPIRED_MODAL_SHOW
        });
      }
      store.dispatch('session/logout');
    }
    throw {
      status: response.status,
      body: responseBody
    };
  }
}

/**
 * Does an HTTP GET request to the specified URL.
 */
async function get(url, config, hostToUse) {
  const urlToUse = getUrl(hostToUse || apiHost, url);
  const response = await fetch(`${urlToUse}${getQueryString(config)}`, getRequestOptions(config));
  return handleResponse(response);
}

/**
 * Sets the IRAPO value indicating the user is an internal staff person
 */
function setIrapo(value) {
  isIrapo = value;
}

export default {
  BASE_API_PATH,
  apiHost: apiHost,
  get,
  getReportApiPath,
  getQueryString,
  async post(url, data, hostToUse) {
    const urlToUse = getUrl(hostToUse || apiHost, url);
    const response = await fetch(urlToUse, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'omit'
    });
    return handleResponse(response);
  },
  async postFile(url, data, hostToUse) {
    const urlToUse = getUrl(hostToUse || apiHost, url);
    const formData = new FormData();
    formData.append('file', data);
    const headers = getHeaders();
    delete headers['Content-Type']; // browser will set the Content-Type automatically
    const response = await fetch(urlToUse, {
      method: 'POST',
      headers: headers,
      body: formData,
      credentials: 'omit'
    });
    return handleResponse(response);
  },
  async put(url, data, hostToUse) {
    const urlToUse = getUrl(hostToUse || apiHost, url);
    const response = await fetch(urlToUse, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'omit'
    });
    return handleResponse(response);
  },
  async delete(url, data, hostToUse) {
    const urlToUse = getUrl(hostToUse || apiHost, url);
    const response = await fetch(urlToUse, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'omit'
    });
    return handleResponse(response);
  },
  // async validate(jwtToken, hostToUse) {
  //   return get(`${BASE_API_PATH}/w/validate`, { jwtToken }, hostToUse);
  // }
  async getRoleForApp(routeQuery, appid) {
    const res = await get('api/d/aa/roles/validate/'+appid, null, null, routeQuery);
    return res;
  },
  setIrapo
};